import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast, Toaster } from "sonner";
import { Pencil, X, Image as ImageIcon, Sparkles } from "lucide-react";
import { resolveImageUrl } from "../../lib/url";
import GalleryPicker from "../../components/GalleryPicker";

const Departments = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/departments").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openEdit = (d) => {
    setForm({
      name: d.name || "",
      description: d.description || "",
      image: d.image || "",
      status: d.status || "active",
      schedule: d.schedule || "",
      location: d.location || "",
      price_per_lesson: d.price_per_lesson ?? 0,
      teacher_share: d.teacher_share ?? 0,
      academy_share: d.academy_share ?? 0,
      featured: !!d.featured,
      levels: (d.levels || []).join(", "),
      sort_order: d.sort_order ?? 99,
    });
    setEditing(d);
  };
  const close = () => setEditing(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price_per_lesson: Number(form.price_per_lesson || 0),
        teacher_share: Number(form.teacher_share || 0),
        academy_share: Number(form.academy_share || 0),
        sort_order: Number(form.sort_order || 99),
        levels: (form.levels || "").split(",").map(s => s.trim()).filter(Boolean),
      };
      await api.put(`/admin/departments/${editing.slug}`, payload);
      toast.success("Fachbereich gespeichert");
      close();
      load();
    } catch {
      toast.error("Fehler beim Speichern");
    } finally { setSaving(false); }
  };

  const grouped = {
    music: items.filter(d => d.category === "music"),
    dance: items.filter(d => d.category === "dance"),
  };

  return (
    <div className="space-y-6" data-testid="admin-departments">
      <Toaster position="top-center" theme="light" />
      <div>
        <h1 className="font-serif-display text-3xl">Fachbereiche</h1>
        <p className="text-sm text-muted-foreground">Front-Bild aus der Galerie wählen, Status & Preise verwalten.</p>
      </div>

      {["music", "dance"].map(cat => (
        <div key={cat}>
          <div className="text-xs uppercase tracking-[0.2em] text-primary mb-3">{cat === "music" ? "School of Music · இசை" : "School of Dance · நாட்டியம்"}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {grouped[cat].map(d => (
              <div key={d.slug} className="card-academy rounded-lg overflow-hidden" data-testid={`dept-card-${d.slug}`}>
                <div className="aspect-video overflow-hidden bg-secondary relative">
                  {d.image ? (
                    <img src={resolveImageUrl(d.image)} alt={d.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImageIcon className="w-8 h-8" /></div>
                  )}
                  {d.featured && (
                    <div className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                      <Sparkles className="w-3 h-3" />Fokus
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-serif-display text-lg">{d.name}</div>
                    <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${d.status === "active" ? "bg-[hsl(var(--brand-green-soft))] text-[hsl(var(--brand-green))]" : "bg-secondary text-muted-foreground"}`}>
                      {d.status === "active" ? "Aktiv" : "Coming Soon"}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">{d.schedule || "—"}</div>
                  <button
                    onClick={() => openEdit(d)}
                    data-testid={`edit-dept-${d.slug}`}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-border text-sm hover:border-primary hover:text-primary transition-colors"
                  >
                    <Pencil className="w-4 h-4" /> Bearbeiten
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={close}>
          <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-card border border-border rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div className="font-serif-display text-xl">{editing.name}</div>
              <button type="button" onClick={close} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-5">
              {/* Front image */}
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Front-Bild</label>
                <div className="flex items-start gap-4">
                  <div className="w-40 h-24 rounded-md overflow-hidden bg-secondary flex-shrink-0">
                    {form.image
                      ? <img src={resolveImageUrl(form.image)} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImageIcon className="w-6 h-6" /></div>}
                  </div>
                  <div className="flex-1 space-y-2">
                    <button
                      type="button"
                      onClick={() => setPickerOpen(true)}
                      data-testid="open-gallery-picker"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-primary/40 text-primary text-sm hover:bg-primary/5"
                    >
                      <ImageIcon className="w-4 h-4" /> Aus Galerie wählen
                    </button>
                    <input
                      value={form.image}
                      onChange={e => setForm({ ...form, image: e.target.value })}
                      placeholder="…oder URL direkt einfügen"
                      className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-xs focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Name" value={form.name} onChange={v => setForm({ ...form, name: v })} required />
                <Field label="Status" type="select" value={form.status} onChange={v => setForm({ ...form, status: v })}
                  options={[{ value: "active", label: "Aktiv" }, { value: "coming_soon", label: "Coming Soon" }]} />
                <Field label="Kurszeiten" value={form.schedule} onChange={v => setForm({ ...form, schedule: v })} />
                <Field label="Ort" value={form.location} onChange={v => setForm({ ...form, location: v })} />
                <Field label="Preis pro Lektion (CHF)" type="number" value={form.price_per_lesson} onChange={v => setForm({ ...form, price_per_lesson: v })} />
                <Field label="Anteil Lehrer" type="number" value={form.teacher_share} onChange={v => setForm({ ...form, teacher_share: v })} />
                <Field label="Anteil Academy" type="number" value={form.academy_share} onChange={v => setForm({ ...form, academy_share: v })} />
                <Field label="Sortierung" type="number" value={form.sort_order} onChange={v => setForm({ ...form, sort_order: v })} />
                <Field label="Levels (Komma-getrennt)" value={form.levels} onChange={v => setForm({ ...form, levels: v })} full placeholder="Einsteiger, Fortgeschrittene" />
              </div>

              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={form.featured} onChange={e => setForm({ ...form, featured: e.target.checked })} className="accent-[hsl(var(--brand-gold))]" />
                <span className="text-sm">Als Fokus-Bereich markieren</span>
              </label>

              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Beschreibung</label>
                <textarea rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary" />
              </div>
            </div>
            <div className="p-5 border-t border-border flex justify-end gap-3">
              <button type="button" onClick={close} className="px-5 py-2 text-sm border border-border rounded-full">Abbrechen</button>
              <button type="submit" disabled={saving} data-testid="dept-save-btn" className="px-6 py-2 text-sm rounded-full btn-gold disabled:opacity-50">{saving ? "…" : "Speichern"}</button>
            </div>
          </form>
        </div>
      )}

      <GalleryPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(url) => setForm(f => ({ ...f, image: url }))}
      />
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", required, full, options, placeholder }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{label}</label>
    {type === "select" ? (
      <select value={value ?? ""} onChange={e => onChange(e.target.value)}
        className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary">
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    ) : (
      <input type={type} value={value ?? ""} onChange={e => onChange(e.target.value)} required={required} placeholder={placeholder}
        className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary" />
    )}
  </div>
);

export default Departments;
